import i18next from 'i18next'
import { get, last, random, sortBy, uniqBy } from 'lodash'

import { BooleanString } from './_types'
import cookie from './cookie'
import { defaultLang } from 'config'
import { EXTERNAL_PATH } from './paths'

/*
 *  todo: combine translation
 *  Server translation
 *  Usage: st('user.title', {name: get(this.props, 'user.data.nickname')})
 */
export const st = (key: string, args = {}) => {
  let text = get(
    window?.gon?.locales,
    `${i18next.language || defaultLang}.${key}`,
    key
  )
  const argsEntries = Object.entries(args)

  if (argsEntries.length) {
    text = argsEntries.reduce((result, current) => {
      return result.replace(`%{${current[0]}}`, current[1]?.toString() || '')
    }, text)
  }

  return text
}

export const unWei = (value: number) => {
  return value / Math.pow(10, 18)
}

export const abbreviateNumber = (value: number) => {
  try {
    // REF: https://jira.nanameue.jp/browse/YAY-12514
    // REF: https://docs.google.com/spreadsheets/d/1AmyfNfHpxzEMHQ_1PfdXrNAVWCk-q-C9rWEx8gfuBWU/edit#gid=0

    const suffixes =
      i18next.language === 'ja'
        ? [
            { power: 8, sign: '億' },
            { power: 4, sign: '万' },
          ]
        : [
            { power: 9, sign: 'B' },
            { power: 6, sign: 'M' },
            { power: 3, sign: 'K' },
          ]

    if (Math.abs(value) < Math.pow(10, last(suffixes).power))
      return value.toLocaleString()

    const isNegative = value < 0
    const absValue = Math.abs(value)
    const suffix = suffixes.find(
      (s) => s.power <= Math.floor(Math.log10(absValue))
    )

    let newValue = (absValue / Math.pow(10, suffix.power)).toLocaleString()
    if (newValue.includes('.')) {
      const dotIndex = newValue.indexOf('.')

      newValue = newValue.substring(
        0,
        newValue[dotIndex + 1] === '0' ? dotIndex : dotIndex + 2
      )

      // REF: https://jira.nanameue.jp/browse/YAY-13790
      if (i18next.language === 'id') {
        newValue = newValue.replace('.', ',')
      }
    }

    return `${isNegative ? '-' : ''}${newValue}${suffix.sign}`
  } catch {
    return value
  }
}

export const limitNumber = (value: number, limit: number) =>
  value > limit ? `${limit}+` : value

export const randomItemsFromList = <T = any>(
  list: T[] = [],
  totalItems: number = 12
) => {
  const total = list.length

  if (total <= totalItems) return list

  const generateFromIndex = random(0, total - totalItems)

  return [...list].slice(generateFromIndex, generateFromIndex + totalItems)
}

export const getMobileOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone'
  }

  if (/android/i.test(userAgent)) {
    return 'Android'
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS'
  }

  return 'unknown'
}

export const openApp = ({ pathname }: { pathname?: string } = {}) => {
  window.location.href =
    getMobileOS?.() === 'iOS'
      ? EXTERNAL_PATH.DOWNLOAD_LINK_IOS
      : EXTERNAL_PATH.DOWNLOAD_LINK_ANDROID

  // This would cause `Address is invalid` popup in iOS Safari.
  // try {
  //   window.location.href = `yay://${window.location.hostname}${
  //     pathname || window.location.pathname
  //   }${window.location.search !== '' ? `${window.location.search}` : ''}`
  // } catch {}
}

export const getUrlsFromString = (text: string = '') => {
  let regx =
    /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)/gi
  return text.match(regx)
}

export const getIsAbleCreateGroup = () => {
  const createGroupLimit = cookie.createGroupLimit.get() || 0

  return createGroupLimit < 5
}

export const toBooleanString = (value: any): BooleanString =>
  value ? 'yes' : 'no'

export const appendList = <
  T extends { id: string | number; created_at?: number; updated_at?: number }
>(
  prevList: T[] = [],
  newList: T[] = [],
  sortByKey = 'created_at'
) => {
  return sortBy(
    uniqBy(
      [
        ...prevList?.map?.((x) => {
          return {
            ...x,
            ...(newList?.find?.((y) => y?.id == x?.id) ?? {}),
          }
        }),
        ...(newList ?? []),
      ],
      (x) => x?.id
    ),
    (x) => x?.[sortByKey] * -1
  )
}

export const breakHighlightText = (text: string = '') =>
  text
    .split(/\s/)
    .map((w) => w.trim())
    .filter((w) => w.length)

export const isSentryWatchedUser = (userId: number) => {
  const WATCHED_USERS = [93, 8510435] // users of interest to log

  return WATCHED_USERS.includes(userId)
}
